import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Power, Save } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const CONDITIONS = [
  { key: "price_above",         label: "Price above" },
  { key: "price_below",         label: "Price below" },
  { key: "score_above",         label: "Score ≥" },
  { key: "entry_zone_reached",  label: "Entry zone reached" },
  { key: "stop_approached",     label: "Stop approached (0.5 ATR)" },
  { key: "stop_breached",       label: "Stop breached" },
  { key: "target_reached",      label: "Target reached (T1)" },
];

// Conditions that don't need a threshold
const NO_THRESHOLD = new Set(["entry_zone_reached", "stop_approached", "stop_breached", "target_reached"]);

/**
 * Minimal alert-rules manager: list existing rules, create one, delete, toggle
 * enabled. Reachable from the notification tray's "Rules" button so we don't
 * need a full Settings page yet.
 */
export default function AlertRulesDialog({ open, onOpenChange, onChanged }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticker, setTicker] = useState("");
  const [condition, setCondition] = useState("price_above");
  const [threshold, setThreshold] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/alerts/rules");
      setRules(r.data?.rules || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load rules.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { load(); setError(""); } }, [open, load]);

  const submit = async () => {
    setError("");
    const t = ticker.trim().toUpperCase();
    if (!t) { setError("Ticker required."); return; }
    const needsThreshold = !NO_THRESHOLD.has(condition);
    const numeric = Number(threshold);
    if (needsThreshold && (threshold === "" || Number.isNaN(numeric))) {
      setError("Threshold required for this condition."); return;
    }
    setSaving(true);
    try {
      await api.post("/alerts/rules", {
        ticker: t,
        condition,
        threshold: needsThreshold ? numeric : null,
        enabled: true,
      });
      setTicker(""); setThreshold("");
      await load();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create rule.");
    } finally { setSaving(false); }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/alerts/rules/${r.id}`, { enabled: !r.enabled });
      await load();
    } catch (e) { setError(e?.response?.data?.detail || "Toggle failed."); }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/alerts/rules/${r.id}`);
      await load();
      onChanged && onChanged();
    } catch (e) { setError(e?.response?.data?.detail || "Delete failed."); }
  };

  const needsThreshold = !NO_THRESHOLD.has(condition);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="alert-rules-dialog"
        className="max-w-[640px] bg-[#0B111C] border border-[#1E293B] p-0"
      >
        <DialogHeader className="px-4 py-3 border-b border-[#1E293B]">
          <DialogTitle className="mono text-sm uppercase tracking-widest text-slate-100">
            Alert Rules
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 border-b border-[#1E293B] bg-[#080B11]">
          <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">New Rule</div>
          <div className="grid grid-cols-[110px_1fr_130px_auto] gap-2 items-end">
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-slate-500">Ticker</label>
              <Input
                data-testid="rule-ticker-input"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                placeholder="NVDA"
                className="mono h-8 bg-[#0F1626] border-[#1E293B]"
              />
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-slate-500">Condition</label>
              <select
                data-testid="rule-condition-select"
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                className="mono text-xs h-8 w-full bg-[#0F1626] border border-[#1E293B] text-slate-100 px-2"
              >
                {CONDITIONS.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-slate-500">
                {needsThreshold ? "Threshold" : "—"}
              </label>
              <Input
                data-testid="rule-threshold-input"
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                disabled={!needsThreshold}
                placeholder={needsThreshold ? "0.00" : "n/a"}
                className="mono h-8 bg-[#0F1626] border-[#1E293B] tabular-nums"
              />
            </div>
            <Button
              data-testid="rule-submit"
              onClick={submit}
              disabled={saving}
              size="sm"
              className="h-8 mono text-[11px] uppercase tracking-widest bg-cyan-500/20 border border-cyan-500/50 text-cyan-200 hover:bg-cyan-500/30"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
              Add
            </Button>
          </div>
          {error && (
            <div data-testid="rule-error" className="mt-2 mono text-[11px] text-red-300 border border-red-500/40 bg-red-500/10 px-2 py-1">
              {error}
            </div>
          )}
        </div>

        <div className="px-4 py-3 max-h-[360px] overflow-y-auto">
          <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
            Active rules ({rules.length})
          </div>
          {loading && rules.length === 0 && (
            <div className="mono text-[11px] text-slate-500 text-center py-4 flex items-center justify-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          )}
          {!loading && rules.length === 0 && (
            <div data-testid="rules-empty" className="mono text-[11px] text-slate-500 text-center py-4">
              No rules yet — create one above.
            </div>
          )}
          <div className="divide-y divide-[#1E293B]">
            {rules.map((r) => (
              <div
                key={r.id}
                data-testid={`rule-row-${r.id}`}
                className={`flex items-center gap-3 py-2 ${r.enabled ? "" : "opacity-50"}`}
              >
                <span className="mono text-sm font-bold text-slate-100 min-w-[64px]">{r.ticker}</span>
                <span className="mono text-[11px] text-slate-300 flex-1 truncate">
                  {CONDITIONS.find((c) => c.key === r.condition)?.label || r.condition}
                  {r.threshold != null && <span className="text-cyan-300 tabular-nums ml-1">{r.threshold}</span>}
                </span>
                <span className={`mono text-[10px] uppercase tracking-widest ${r.enabled ? "text-emerald-300" : "text-slate-500"}`}>
                  {r.enabled ? "on" : "off"}
                </span>
                <button
                  data-testid={`rule-toggle-${r.id}`}
                  onClick={() => toggle(r)}
                  title="Toggle enabled"
                  className="h-7 w-7 flex items-center justify-center border border-[#1E293B] text-slate-400 hover:text-slate-100 hover:border-cyan-500/40"
                >
                  <Power className="h-3 w-3" />
                </button>
                <button
                  data-testid={`rule-delete-${r.id}`}
                  onClick={() => remove(r)}
                  title="Delete rule"
                  className="h-7 w-7 flex items-center justify-center border border-[#1E293B] text-slate-400 hover:text-red-300 hover:border-red-500/40"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
